import { useEffect, useState } from 'react';
import { getCityNames, getDistrictsByCity } from '@/data/turkeyLocations';
import { getNeighborhoods } from '@/data/turkeyNeighborhoods';

interface CityDistrictSelectProps {
  city: string;
  district: string;
  onCityChange: (city: string) => void;
  onDistrictChange: (district: string) => void;
  /** Verilirse üçüncü bir "Mahalle" select'i render edilir (her zaman opsiyoneldir). */
  neighborhood?: string;
  onNeighborhoodChange?: (neighborhood: string) => void;
  cityLabel?: string;
  districtLabel?: string;
  neighborhoodLabel?: string;
  className?: string;
  selectClassName?: string;
  required?: boolean;
  disabled?: boolean;
}

const defaultSelectClass = 'w-full px-3.5 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-60';

/**
 * Türkiye il/ilçe(/mahalle) seçimi için merkezi, tekrar kullanılabilir bileşen.
 * İl değişince ilçe (ve mahalle) listesi otomatik filtrelenir/sıfırlanır;
 * ilçe değişince mahalle sıfırlanır. Mahalle her zaman opsiyoneldir — bu
 * yüzden `required` mahalle select'ine yansımaz. Veri kaynağı:
 * src/data/turkeyLocations.ts (il/ilçe, senkron) ve
 * src/data/turkeyNeighborhoods.ts (mahalle, ~560KB'lık tam veri seti
 * dinamik import() ile yalnızca il+ilçe seçildiğinde bir kez indirilir —
 * ana bundle'ı şişirmez). onNeighborhoodChange verilmezse mahalle select'i
 * hiç render edilmez — mevcut çağıranlar değişiklik yapmadan aynı şekilde
 * çalışır.
 */
export default function CityDistrictSelect({
  city,
  district,
  onCityChange,
  onDistrictChange,
  neighborhood,
  onNeighborhoodChange,
  cityLabel = 'İl',
  districtLabel = 'İlçe',
  neighborhoodLabel = 'Mahalle',
  className,
  selectClassName,
  required = false,
  disabled = false,
}: CityDistrictSelectProps) {
  const cities = getCityNames();
  const districts = city ? getDistrictsByCity(city) : [];
  const showNeighborhood = !!onNeighborhoodChange;
  const selectClass = selectClassName || defaultSelectClass;
  const gridClass = className || (showNeighborhood ? 'grid grid-cols-1 sm:grid-cols-3 gap-3' : 'grid grid-cols-2 gap-3');

  const [neighborhoods, setNeighborhoods] = useState<string[]>([]);
  const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(false);

  useEffect(() => {
    if (!showNeighborhood || !city || !district) {
      setNeighborhoods([]);
      return;
    }
    let cancelled = false;
    setLoadingNeighborhoods(true);
    getNeighborhoods(city, district).then((list) => {
      if (!cancelled) {
        setNeighborhoods(list);
        setLoadingNeighborhoods(false);
      }
    });
    return () => { cancelled = true; };
  }, [showNeighborhood, city, district]);

  const handleCityChange = (newCity: string) => {
    onCityChange(newCity);
    onDistrictChange('');
    onNeighborhoodChange?.('');
  };

  const handleDistrictChange = (newDistrict: string) => {
    onDistrictChange(newDistrict);
    onNeighborhoodChange?.('');
  };

  const neighborhoodPlaceholder = !district
    ? 'Önce ilçe seçiniz'
    : loadingNeighborhoods
    ? 'Yükleniyor...'
    : neighborhoods.length
    ? 'Mahalle seçiniz'
    : 'Mahalle bulunamadı';

  return (
    <div className={gridClass}>
      <div>
        <label className="block text-sm font-medium text-foreground-700 mb-1.5">
          {cityLabel}{required && <span className="text-red-500"> *</span>}
        </label>
        <select
          value={city}
          onChange={(e) => handleCityChange(e.target.value)}
          className={selectClass}
          disabled={disabled}
        >
          <option value="">İl seçiniz</option>
          {cities.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground-700 mb-1.5">
          {districtLabel}{required && <span className="text-red-500"> *</span>}
        </label>
        <select
          value={district}
          onChange={(e) => handleDistrictChange(e.target.value)}
          className={selectClass}
          disabled={disabled || !city}
        >
          <option value="">{city ? 'İlçe seçiniz' : 'Önce il seçiniz'}</option>
          {districts.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      </div>
      {showNeighborhood && (
        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">
            {neighborhoodLabel} <span className="text-foreground-300 font-normal">(opsiyonel)</span>
          </label>
          <select
            value={neighborhood || ''}
            onChange={(e) => onNeighborhoodChange?.(e.target.value)}
            className={selectClass}
            disabled={disabled || !district || loadingNeighborhoods}
          >
            <option value="">{neighborhoodPlaceholder}</option>
            {neighborhoods.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
